import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, Lock, CreditCard, Wallet, Gift, Sparkles } from "lucide-react";
import { api, MANUAL_PAYMENT } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function CartPage() {
  const { items, updateQty, removeItem, subtotal, clear, addItem } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [method, setMethod] = useState("manual");
  const [promo, setPromo] = useState("");
  const [discount, setDiscount] = useState(null);
  const [applying, setApplying] = useState(false);
  const [shipMethods, setShipMethods] = useState([]);
  const [shipMethodId, setShipMethodId] = useState("");
  const [rewards, setRewards] = useState(null);
  const [redeemApplied, setRedeemApplied] = useState(false);
  const [sale, setSale] = useState(null);
  const [upsell, setUpsell] = useState(null);
  const [upsellOpen, setUpsellOpen] = useState(false);
  const [pendingCheckout, setPendingCheckout] = useState(null);
  const [guestPromoOpen, setGuestPromoOpen] = useState(false);
  const [form, setForm] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("jps_checkout_form") || "null");
      if (saved && (saved.email || saved.shipping_name || saved.shipping_address)) return saved;
    } catch {
      /* ignore */
    }
    return { email: user?.email || "", shipping_name: user?.name || "", shipping_address: "" };
  });

  // Persist the shipping form so it survives the round-trip to Stripe and back.
  useEffect(() => {
    localStorage.setItem("jps_checkout_form", JSON.stringify(form));
  }, [form]);

  // Reset the loading state when returning to the page (incl. browser back / bfcache restore),
  // so the pay button is never stuck on "Redirecting…".
  useEffect(() => {
    const onShow = () => setLoading(false);
    window.addEventListener("pageshow", onShow);
    return () => window.removeEventListener("pageshow", onShow);
  }, []);

  useEffect(() => {
    api.get("/shipping-methods").then(({ data }) => {
      setShipMethods(data);
      setShipMethodId((prev) => prev || (data[0]?.id ?? ""));
    }).catch(() => {});
  }, []);

  // Load reward balance for logged-in customers.
  useEffect(() => {
    if (!user) { setRewards(null); setRedeemApplied(false); return; }
    api.get("/rewards/me").then(({ data }) => setRewards(data)).catch(() => setRewards(null));
  }, [user]);

  // Load admin-configured upsell add-ons + one-time guest signup nudge.
  useEffect(() => {
    api.get("/settings/upsell").then(({ data }) => setUpsell(data)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/settings/sale").then(({ data }) => setSale(data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (user) return;
    if (items.length === 0) return;
    if (sessionStorage.getItem("jps_rewards_nudge")) return;
    const t = setTimeout(() => {
      setGuestPromoOpen(true);
      sessionStorage.setItem("jps_rewards_nudge", "1");
    }, 900);
    return () => clearTimeout(t);
  }, [user, items.length]);

  const saleActive = !!sale?.active;
  const codesAllowed = !saleActive || sale?.allow_discount_codes !== false;
  const saleAmtRaw = !saleActive ? 0 : sale.type === "percent" ? (subtotal * sale.value) / 100 : Math.min(subtotal, sale.value);
  const codeRawOn = (base) => (!discount ? 0 : discount.type === "percent" ? (base * discount.value) / 100 : Math.min(base, discount.value));

  let saleApplied = 0;
  let discountAmount = 0;
  let afterDiscount = subtotal;
  if (!saleActive) {
    discountAmount = codeRawOn(subtotal);
    afterDiscount = Math.max(0, subtotal - discountAmount);
  } else if (!discount || !codesAllowed) {
    saleApplied = saleAmtRaw;
    afterDiscount = Math.max(0, subtotal - saleApplied);
  } else if (sale.code_combine === "stack") {
    saleApplied = saleAmtRaw;
    const afterSale = Math.max(0, subtotal - saleApplied);
    discountAmount = codeRawOn(afterSale);
    afterDiscount = Math.max(0, afterSale - discountAmount);
  } else {
    const codeFull = codeRawOn(subtotal);
    if (codeFull >= saleAmtRaw) { discountAmount = codeFull; afterDiscount = Math.max(0, subtotal - codeFull); }
    else { saleApplied = saleAmtRaw; afterDiscount = Math.max(0, subtotal - saleAmtRaw); }
  }
  const codeApplied = saleActive && !codesAllowed ? false : !!discount;
  const canRedeem = !!(user && rewards && rewards.can_redeem && afterDiscount > 0);
  const rewardDiscount = canRedeem && redeemApplied ? Math.min(rewards.balance, afterDiscount) : 0;
  const total = Math.max(0, afterDiscount - rewardDiscount);
  const selectedShip = shipMethods.find((m) => m.id === shipMethodId) || null;
  const shippingCost = !selectedShip ? 0 : selectedShip.free_over != null && total >= selectedShip.free_over ? 0 : Number(selectedShip.price || 0);
  const grandTotal = Math.max(0, total + shippingCost);
  const addonProducts = (upsell?.enabled ? (upsell.products || []) : []).filter((p) => !items.some((i) => i.slug === p.slug) && Number(p.stock) > 0);
  const hasBacWater = addonProducts.some((p) => /bacteriostatic|bac[-\s]?water/i.test(`${p.slug} ${p.name}`));
  const addressComplete = (() => {
    const a = (form.shipping_address || "").trim();
    return /\b\d{5}(-\d{4})?\b/.test(a) && a.length >= 10;
  })();

  const applyPromo = async () => {
    if (!promo.trim()) return;
    setApplying(true);
    try {
      const { data } = await api.post("/discounts/validate", { code: promo.trim() });
      setDiscount(data);
      toast.success(`Code ${data.code} applied`);
    } catch {
      setDiscount(null);
      toast.error("Invalid or expired code");
    } finally {
      setApplying(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    const email = form.email?.trim();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email) || items.length === 0) return;
    const t = setTimeout(() => {
      api.post("/cart/track", {
        email,
        items: items.map((i) => ({ product_id: i.product_id, slug: i.slug, name: i.name, price: i.price, quantity: i.quantity })),
        origin_url: window.location.origin,
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [form.email, items]);

  const validate = () => {
    if (!form.email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) {
      toast.error("Please enter a valid email address");
      return false;
    }
    if (!form.shipping_name?.trim()) {
      toast.error("Please enter your full name");
      return false;
    }
    const addr = (form.shipping_address || "").trim();
    if (!addr) {
      toast.error("Please enter your shipping address");
      return false;
    }
    if (!/\b\d{5}(-\d{4})?\b/.test(addr)) {
      toast.error("Your shipping address is missing a ZIP code. Please enter the full address — street, city, state and ZIP.");
      return false;
    }
    if (addr.length < 10) {
      toast.error("Please enter your full shipping address — street, city, state and ZIP.");
      return false;
    }
    return true;
  };

  const checkout = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        items: items.map((i) => ({ product_id: i.product_id, slug: i.slug, name: i.name, price: i.price, quantity: i.quantity })),
        origin_url: window.location.origin,
        email: form.email,
        shipping_name: form.shipping_name,
        shipping_address: form.shipping_address,
        discount_code: discount?.code || null,
        shipping_method_id: shipMethodId || null,
        redeem_amount: rewardDiscount > 0 ? rewards.balance : 0,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  const placeManualOrder = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/manual", {
        items: items.map((i) => ({ product_id: i.product_id, slug: i.slug, name: i.name, price: i.price, quantity: i.quantity })),
        email: form.email,
        shipping_name: form.shipping_name,
        shipping_address: form.shipping_address,
        discount_code: discount?.code || null,
        shipping_method_id: shipMethodId || null,
        redeem_amount: rewardDiscount > 0 ? rewards.balance : 0,
      });
      clear();
      localStorage.removeItem("jps_checkout_form");
      navigate(`/checkout/success?manual=1&oid=${data.order_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not place your order. Please try again.");
      setLoading(false);
    }
  };

  // Show the add-on upsell once before running the real checkout action.
  const startCheckout = (fn) => {
    if (!validate()) return;
    if (addonProducts.length > 0) {
      setPendingCheckout(() => fn);
      setUpsellOpen(true);
      return;
    }
    fn();
  };
  const continueFromUpsell = () => {
    setUpsellOpen(false);
    const fn = pendingCheckout;
    setPendingCheckout(null);
    if (fn) fn();
  };

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-32 text-center">
        <Seo title="Cart | Just Peptides Supply" path="/cart" noindex />
        <h1 className="font-display text-3xl text-brand-navy">Your cart is empty</h1>
        <Link to="/shop"><Button className="mt-6 rounded-full bg-brand-navy hover:bg-brand-blue text-white px-7 h-12" data-testid="empty-cart-shop">Browse Peptides</Button></Link>
      </div>
    );
  }

  return (
    <>
      <Seo title="Checkout | Just Peptides Supply" path="/cart" noindex />
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-14">
        <h1 className="font-display text-4xl text-brand-navy tracking-tight mb-10">Checkout</h1>

        <div className="grid gap-10 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <div className="space-y-4">
              {items.map((i) => (
                <div key={i.slug} className="flex gap-4 rounded-xl border border-brand-navy/10 bg-white p-4" data-testid={`checkout-item-${i.slug}`}>
                  <img src={i.image} alt={i.name} className="h-24 w-24 rounded-lg object-cover bg-brand-mist" />
                  <div className="flex-1">
                    <p className="font-semibold text-brand-navy">{i.name}</p>
                    <p className="text-brand-blue font-bold mt-1">${i.price.toFixed(2)}</p>
                    <div className="flex items-center gap-3 mt-3">
                      <div className="flex items-center border border-brand-navy/15 rounded-full">
                        <button onClick={() => updateQty(i.slug, i.quantity - 1)} className="p-1.5"><Minus className="h-3.5 w-3.5" /></button>
                        <span className="w-7 text-center text-sm font-semibold">{i.quantity}</span>
                        <button onClick={() => updateQty(i.slug, i.quantity + 1)} className="p-1.5"><Plus className="h-3.5 w-3.5" /></button>
                      </div>
                      <button onClick={() => removeItem(i.slug)} className="text-brand-navy/40 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                  <p className="font-display text-lg text-brand-navy">${(i.price * i.quantity).toFixed(2)}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-xl border border-brand-navy/10 bg-white p-6">
              <h2 className="font-display text-xl text-brand-navy mb-5">Shipping details</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={form.email} onChange={set("email")} className="mt-1.5" data-testid="checkout-email" placeholder="you@email.com" />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="name">Full name</Label>
                  <Input id="name" value={form.shipping_name} onChange={set("shipping_name")} className="mt-1.5" data-testid="checkout-name" placeholder="Jane Researcher" />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="addr">Shipping address</Label>
                  <Input id="addr" value={form.shipping_address} onChange={set("shipping_address")} className={`mt-1.5 ${form.shipping_address && !addressComplete ? "border-amber-500 focus-visible:ring-amber-500" : ""}`} data-testid="checkout-address" placeholder="123 Lab St, City, State ZIP" />
                  {form.shipping_address && !addressComplete && (
                    <p className="mt-1.5 text-xs text-amber-600" data-testid="address-hint">Please include street, city, state and ZIP code.</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="rounded-xl border border-brand-navy/10 bg-brand-mist p-6 sticky top-28">
              <h2 className="font-display text-xl text-brand-navy mb-5">Order summary</h2>
              <div className="space-y-2.5 text-sm text-brand-navy/70">
                <div className="flex justify-between"><span>Subtotal</span><span data-testid="summary-subtotal">${subtotal.toFixed(2)}</span></div>
                {saleApplied > 0 && (
                  <div className="flex justify-between text-brand-teal font-semibold" data-testid="summary-sale">
                    <span>{sale?.name || "Sale"}</span><span>−${saleApplied.toFixed(2)}</span>
                  </div>
                )}
                {codeApplied && discountAmount > 0 && (
                  <div className="flex justify-between text-brand-teal font-semibold" data-testid="summary-discount">
                    <span>Discount ({discount.code})</span><span>−${discountAmount.toFixed(2)}</span>
                  </div>
                )}
                {rewardDiscount > 0 && (
                  <div className="flex justify-between text-brand-teal font-semibold" data-testid="summary-rewards">
                    <span>Rewards credit</span><span>−${rewardDiscount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Shipping</span>
                  <span data-testid="summary-shipping">{selectedShip ? (shippingCost === 0 ? <span className="text-brand-teal font-semibold">FREE</span> : `$${shippingCost.toFixed(2)}`) : "—"}</span>
                </div>
              </div>

              <div className="mt-4">
                <p className="font-mono-label text-[11px] uppercase tracking-[0.18em] text-brand-navy/50 mb-2">Shipping method</p>
                <div className="space-y-2" data-testid="shipping-methods">
                  {shipMethods.length === 0 && <p className="text-xs text-brand-navy/50">Loading shipping options…</p>}
                  {shipMethods.map((m) => {
                    const free = m.free_over != null && total >= m.free_over;
                    const active = m.id === shipMethodId;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setShipMethodId(m.id)}
                        data-testid={`shipping-method-${m.id}`}
                        className={`w-full flex items-center gap-3 rounded-xl border px-3.5 py-2.5 text-left transition-colors ${active ? "border-brand-blue bg-brand-blue/5" : "border-brand-navy/15 bg-white hover:border-brand-navy/30"}`}
                      >
                        <span className={`h-4 w-4 rounded-full border-2 shrink-0 ${active ? "border-brand-blue bg-brand-blue" : "border-brand-navy/25"}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-brand-navy truncate">{m.name}</p>
                          {m.description && <p className="text-xs text-brand-navy/55 truncate">{m.description}</p>}
                        </div>
                        <span className="text-sm font-semibold text-brand-navy shrink-0">{free ? "FREE" : `$${Number(m.price).toFixed(2)}`}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-4">
                {!codesAllowed ? (
                  <div className="rounded-xl border border-brand-teal/30 bg-brand-teal/5 p-3 text-xs text-brand-navy/70" data-testid="promo-disabled">
                    Promo codes are paused during the {sale?.name || "current sale"}.
                  </div>
                ) : !discount ? (
                  <div className="flex gap-2">
                    <Input value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} placeholder="Promo code" className="h-10 bg-white" data-testid="promo-input" />
                    <Button onClick={applyPromo} disabled={applying} variant="outline" className="rounded-full shrink-0" data-testid="promo-apply">{applying ? "…" : "Apply"}</Button>
                  </div>
                ) : (
                  <button onClick={() => { setDiscount(null); setPromo(""); }} className="text-xs text-brand-navy/50 hover:text-destructive underline" data-testid="promo-remove">Remove code</button>
                )}
              </div>

              {user && rewards && rewards.enabled && (rewards.can_redeem ? (
                <div className="mt-4 rounded-xl border border-brand-teal/30 bg-brand-teal/5 p-3.5" data-testid="rewards-redeem">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={redeemApplied}
                      onChange={(e) => setRedeemApplied(e.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-brand-teal"
                      data-testid="rewards-redeem-toggle"
                    />
                    <span className="text-sm">
                      <span className="font-semibold text-brand-navy flex items-center gap-1.5"><Gift className="h-4 w-4 text-brand-teal" /> Apply my rewards credit</span>
                      <span className="text-brand-navy/60">You have <b>${rewards.balance.toFixed(2)}</b> in rewards to spend on this order.</span>
                    </span>
                  </label>
                </div>
              ) : (
                <div className="mt-4 rounded-xl border border-brand-navy/10 bg-white p-3.5 text-sm text-brand-navy/70" data-testid="rewards-progress">
                  <span className="font-semibold text-brand-navy flex items-center gap-1.5"><Gift className="h-4 w-4 text-brand-teal" /> ${rewards.balance.toFixed(2)} in rewards</span>
                  <span className="text-brand-navy/55">Earn ${rewards.earn_reward.toFixed(2)} back for every ${rewards.earn_spend.toFixed(2)} you spend. Reach ${rewards.min_redeem.toFixed(2)} to start redeeming.</span>
                </div>
              ))}

              <div className="flex justify-between items-center border-t border-brand-navy/15 mt-4 pt-4">
                <span className="font-semibold text-brand-navy">Total</span>
                <span className="font-display text-2xl text-brand-navy" data-testid="summary-total">${grandTotal.toFixed(2)}</span>
              </div>

              <div className="mt-6">
                <p className="font-mono-label text-[11px] uppercase tracking-[0.18em] text-brand-navy/50 mb-3">Payment method</p>
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setMethod("manual")}
                    data-testid="method-manual"
                    className={`w-full flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${method === "manual" ? "border-brand-blue bg-brand-blue/5" : "border-brand-navy/15 bg-white hover:border-brand-navy/30"}`}
                  >
                    <Wallet className="h-5 w-5 text-brand-navy shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-brand-navy">Manual Payment</p>
                      <p className="text-xs text-brand-navy/55">Zelle · Venmo · Cash App</p>
                    </div>
                    <span className={`h-4 w-4 rounded-full border-2 ${method === "manual" ? "border-brand-blue bg-brand-blue" : "border-brand-navy/25"}`} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setMethod("card")}
                    data-testid="method-card"
                    className={`w-full flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${method === "card" ? "border-brand-blue bg-brand-blue/5" : "border-brand-navy/15 bg-white hover:border-brand-navy/30"}`}
                  >
                    <CreditCard className="h-5 w-5 text-brand-navy shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-brand-navy">Credit / Debit Card</p>
                      <p className="text-xs text-brand-navy/55">Secure checkout by Stripe</p>
                    </div>
                    <span className={`h-4 w-4 rounded-full border-2 ${method === "card" ? "border-brand-blue bg-brand-blue" : "border-brand-navy/25"}`} />
                  </button>
                </div>
              </div>

              {method === "manual" && (
                <div className="mt-4 rounded-xl border border-brand-navy/15 bg-white p-4 text-sm text-brand-navy/80" data-testid="manual-payment-details">
                  <div className="space-y-1.5">
                    <div className="flex justify-between gap-3"><span className="text-brand-navy/55">Zelle</span><span className="font-semibold text-brand-navy">{MANUAL_PAYMENT.zelle}</span></div>
                    <div className="flex justify-between gap-3"><span className="text-brand-navy/55">Venmo</span><span className="font-semibold text-brand-navy">{MANUAL_PAYMENT.venmo}</span></div>
                    <div className="flex justify-between gap-3"><span className="text-brand-navy/55">Cash App</span><span className="font-semibold text-brand-navy">{MANUAL_PAYMENT.cashapp}</span></div>
                    <div className="flex justify-between gap-3"><span className="text-brand-navy/55">Email</span><span className="font-semibold text-brand-navy break-all">{MANUAL_PAYMENT.email}</span></div>
                  </div>
                  <p className="mt-3 pt-3 border-t border-brand-navy/10 text-xs leading-relaxed text-brand-navy/60">{MANUAL_PAYMENT.note}</p>
                  <p className="mt-2 text-xs leading-relaxed text-brand-navy/60">{MANUAL_PAYMENT.instagram}</p>
                </div>
              )}

              {method === "manual" ? (
                <Button onClick={() => startCheckout(placeManualOrder)} disabled={loading} data-testid="place-order-button" className="mt-6 w-full rounded-full bg-brand-navy hover:bg-brand-blue text-white h-13 py-3.5 text-base">
                  {loading ? "Placing order…" : "Place Order & Pay"}
                </Button>
              ) : (
                <Button onClick={() => startCheckout(checkout)} disabled={loading} data-testid="pay-button" className="mt-6 w-full rounded-full bg-brand-navy hover:bg-brand-blue text-white h-13 py-3.5 text-base">
                  {loading ? "Redirecting…" : (<><CreditCard className="mr-2 h-4 w-4" /> Pay with Card</>)}
                </Button>
              )}
              <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-brand-navy/50">
                <Lock className="h-3.5 w-3.5" /> {method === "manual" ? "Your order is recorded securely" : "Secure checkout by Stripe"}
              </p>
            </div>
          </div>
        </div>
      </section>

      <Dialog open={upsellOpen} onOpenChange={(v) => { if (!v) { setUpsellOpen(false); setPendingCheckout(null); } }}>
        <DialogContent className="max-w-lg" data-testid="upsell-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-brand-navy flex items-center gap-2"><Sparkles className="h-5 w-5 text-brand-teal" /> {hasBacWater ? "Do you have Bacteriostatic Water?" : "You might also need"}</DialogTitle>
            <DialogDescription>{hasBacWater ? "Bacteriostatic water is required to reconstitute your peptides. If you don't already have some, add it now — one tap." : "Add these research essentials before you check out."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[50vh] overflow-y-auto">
            {addonProducts.map((p) => (
              <div key={p.slug} className="flex items-center gap-4 rounded-xl border border-brand-navy/10 bg-white p-3" data-testid={`upsell-item-${p.slug}`}>
                <img src={p.image} alt={p.name} className="h-16 w-16 rounded-lg object-cover bg-brand-mist" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-brand-navy truncate">{p.name}</p>
                  <p className="text-brand-blue font-bold text-sm">${Number(p.price).toFixed(2)}</p>
                </div>
                <Button onClick={() => { addItem(p, 1); toast.success(`${p.name} added`); }} size="sm" className="rounded-full bg-brand-teal hover:bg-brand-teal/90 text-white gap-1.5 shrink-0" data-testid={`upsell-add-${p.slug}`}>
                  <Plus className="h-3.5 w-3.5" /> Add
                </Button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={continueFromUpsell} className="w-full rounded-full bg-brand-navy hover:bg-brand-blue text-white" data-testid="upsell-continue">No thanks, continue to checkout</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={guestPromoOpen} onOpenChange={setGuestPromoOpen}>
        <DialogContent className="max-w-md" data-testid="guest-rewards-dialog">
          <DialogHeader>
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-brand-teal/15">
              <Gift className="h-6 w-6 text-brand-teal" />
            </div>
            <DialogTitle className="text-center font-display text-2xl text-brand-navy">Earn rewards on this order</DialogTitle>
            <DialogDescription className="text-center">
              Create a free account and earn points on every purchase — redeem them for money off future orders. It only takes a moment.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button onClick={() => navigate("/auth")} className="w-full rounded-full bg-brand-navy hover:bg-brand-blue text-white" data-testid="guest-rewards-signup">Sign up &amp; start earning</Button>
            <Button onClick={() => setGuestPromoOpen(false)} variant="outline" className="w-full rounded-full" data-testid="guest-rewards-dismiss">Maybe later</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
